import { useState, useRef } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useForm, Controller, useController } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { CORPORATION_USER_TYPE, INDIVIDUAL_USER_TYPE, COUNTRY_LIST, PROVINCES_LIST } from 'constants'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { Avatar, CardActionArea, CardHeader } from '@mui/material'

import { BuildingColumns, Business, CircleUser, HandHoldingHeart } from '../components/icons/icons'
import { useBrandingContext } from '../context'
import { parseAndSetFormErrors, captureTrackingData } from '../utils'
import {
  Acknowledge,
  Button,
  Container,
  ContentBox,
  Fields,
  InputLabel,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Row,
  Select,
  Stack,
  Switch,
  Text,
  useAlert,
  Card,
  Divider,
} from '../components'
import { validations } from '../components/form'
import { mutations } from '../graphql'
import { SIGNUP_TYPE } from '../constants'
import { OtherAdvisorInvitesPrompt } from './claim-fund/other-advisor-invites-prompt'

export function SignUpSuccess() {
  const branding = useBrandingContext() || {}
  const platformName = branding.platformName || 'GiveWise'
  const supportEmail = branding.supportEmail || 'support@givewise.ca'
  const location = useLocation()
  const otherPendingInvites = location.state?.otherPendingInvites

  return (
    <Container maxWidth="sm">
      <Acknowledge.Success
        title={`Welcome to ${platformName} and our community of generous givers!`}
        content={
          <Text.Body>
            Please check your email for a link to confirm your email and log in to your account. If you do not receive a
            confirmation email, please check your junk mail or email{' '}
            <Link href={`mailto:${supportEmail}?subject=Confirmation%20Email%20Assistance`}>{supportEmail}</Link> for
            assistance.
          </Text.Body>
        }
      />
      <OtherAdvisorInvitesPrompt invites={otherPendingInvites} useDirectMutations />
    </Container>
  )
}

export function SignUp() {
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const { platformName, primaryColor } = useBrandingContext() || {}
  const location = useLocation()
  const navigate = useNavigate()
  const { selectedSignupType, afterLogin } = location.state ?? {}
  const isCorporation = selectedSignupType === SIGNUP_TYPE.business
  const { register, handleSubmit, formState, setError, getValues, setValue, watch, control } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      isOwnAccount: 'yes',
      ...(JSON.parse(localStorage.getItem('guest_user_prefill_info') || 'null') || {
        address: {
          country: 'Canada',
        },
      }),
    },
  })
  // Individuals only: when the account is for someone else we collect that person's name for the Giving Fund
  const isForSomeoneElse = !isCorporation && watch('isOwnAccount') === 'no'
  const isOwnAddress = !isCorporation && !isForSomeoneElse
  let addressHeading = 'Your Address'
  if (isCorporation) addressHeading = 'Organization Address'
  else if (isForSomeoneElse) addressHeading = 'Donor Address'

  // Capture UTM parameters and external referrer (first-touch attribution)
  const trackingData = captureTrackingData()
  const { errors = {} } = formState
  const captchaRef = useRef()
  const [captchaToken, setCaptchaToken] = useState()
  const { data: { captcha: captchaSiteKey } = {}, refetch: refetchCaptcha } = useQuery(gql`
    query Captcha {
      captcha
    }
  `)

  const [signUp, { loading }] = useMutation(mutations.user.signUp, {
    onCompleted: async () => {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'signupSuccess',
          method: 'sign-up-form',
          userType: selectedSignupType === SIGNUP_TYPE.business ? 'organization' : 'personal',
        })
      }

      navigate('/sign-up/success')
    },
    onError: async (newErrors) => {
      parseAndSetFormErrors(newErrors, setError)

      /* if we submitted a captcha, the server likely verified it and it can't
       * be used again; so reset the widget ... */
      captchaRef?.current?.resetCaptcha?.()

      if (newErrors?.message.includes('Email taken')) {
        setAlert({ message: 'An account with this email address already exists.', severity: 'error' })
      } else if (newErrors?.message === 'captcha required') {
        setAlert({ message: 'Please fill out the captcha on this form.', severity: 'error' })
        /* if we a captcha was not required before, it is now */
        if (!captchaSiteKey) await refetchCaptcha()
      } else {
        setAlert({ message: 'Signup Failed', severity: 'error' })
      }
    },
  })

  const onSubmit = ({
    firstName,
    middleInitials,
    lastName,
    email,
    phone,
    password,
    passwordConfirmation,
    corporateName,
    donorName,
    isOwnAccount,
    address,
  }) => {
    signUp({
      variables: {
        data: {
          firstName,
          middleInitials,
          lastName,
          corporateName: isCorporation ? corporateName : undefined,
          // Otherwise the API derives the Giving Fund / tax receipt name from the organization or the user
          donorName: !isCorporation && isOwnAccount === 'no' ? donorName : undefined,
          email,
          phone,
          password,
          passwordConfirmation,
          address,
          userType: selectedSignupType === SIGNUP_TYPE.business ? CORPORATION_USER_TYPE : INDIVIDUAL_USER_TYPE,
          afterLogin,
          captcha: captchaToken,
          ...trackingData,
        },
      },
    })

    localStorage.removeItem('guest_user_prefill_info')
  }

  const signUpForm = () => (
    <ContentBox border>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControlLabel
          size="small"
          control={<Switch />}
          checked={isCorporation}
          onChange={(e) =>
            navigate(
              { search: location.search },
              {
                state: { selectedSignupType: e.target.checked ? SIGNUP_TYPE.business : SIGNUP_TYPE.personal },
                replace: true,
              }
            )
          }
          label="Sign up as an Organization or Business"
          sx={{ mb: 2 }}
        />
        <Text.H6>User Details</Text.H6>
        <Text.Body sx={{ mb: 2 }}>
          {isCorporation
            ? 'These are the details used to access and manage your GiveWise account.'
            : 'These are your details to access and manage your GiveWise account.'}
        </Text.Body>
        <Stack alignItems="flex-start">
          {isCorporation && (
            <>
              <Fields.Text
                inputProps={{ 'data-testid': 'corporateName' }}
                label="Organization/Business Name *"
                error={!!errors.corporateName}
                helperText={errors.corporateName?.message}
                {...register('corporateName', { ...validations.required })}
              />
              <Fields.Text
                inputProps={{ 'data-testid': 'email' }}
                label="Email *"
                error={!!errors.email}
                helperText={errors.email?.message}
                {...register('email', { ...validations.required, ...validations.email })}
              />
              <Fields.NewPassword register={register} errors={errors} getValues={getValues} />
              <Text.Bold>Primary Contact Person</Text.Bold>
              <Text.Body>Who should GiveWise contact about this account?</Text.Body>
            </>
          )}
          <Fields.Text
            inputProps={{ 'data-testid': 'firstName' }}
            label="First Name *"
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
            {...register('firstName', { ...validations.required })}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'middleInitial' }}
            label="Middle Initials"
            error={!!errors.middleInitial}
            helperText={errors.middleInitial?.message}
            {...register('middleInitials')}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'lastName' }}
            label="Last Name *"
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
            {...register('lastName', { ...validations.required })}
          />
          {!isCorporation && (
            <Fields.Text
              inputProps={{ 'data-testid': 'email' }}
              label="Email *"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email', { ...validations.required, ...validations.email })}
            />
          )}
          <Fields.Text
            inputProps={{ 'data-testid': 'phoneNumber' }}
            type="text"
            label="Phone Number"
            required
            error={!!errors.phone}
            helperText={errors?.phone?.message}
            {...register('phone', { ...validations.required, ...validations.phoneNumber })}
          />
          {!isCorporation && <Fields.NewPassword register={register} errors={errors} getValues={getValues} />}

          {!isCorporation && (
            <>
              <Text.H6>Is this account for you?</Text.H6>
              <Controller
                control={control}
                name="isOwnAccount"
                render={({ field }) => (
                  <FormControl>
                    <RadioGroup {...field} data-testid="isOwnAccount" sx={{ gap: 3 }}>
                      <FormControlLabel
                        value="yes"
                        control={<Radio inputProps={{ 'data-testid': 'isOwnAccountYes' }} />}
                        label={
                          <>
                            <Text.Bold>Yes, this account is for me</Text.Bold>
                            <Text.Body>Use my details for my Giving Fund and tax receipts.</Text.Body>
                          </>
                        }
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio inputProps={{ 'data-testid': 'isOwnAccountNo' }} />}
                        label={
                          <>
                            <Text.Bold>No, I&rsquo;m setting it up for someone else</Text.Bold>
                            <Text.Body>I&rsquo;ll enter their details next.</Text.Body>
                          </>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </>
          )}

          {isForSomeoneElse && (
            <>
              <Text.H6>Donor Details</Text.H6>
              <Text.Body>
                These are the details of the individual whose Giving Fund you are setting up. Their name will appear on
                tax receipts.
              </Text.Body>
              <Fields.Text
                inputProps={{ 'data-testid': 'donorName' }}
                label="Donor Name *"
                error={!!errors.donorName}
                helperText={errors.donorName?.message}
                {...register('donorName', { ...validations.required })}
              />
            </>
          )}

          <Text.Bold>{addressHeading}</Text.Bold>
          <Text.Body>
            {isOwnAddress
              ? 'Your address is required for tax receipts. No physical mail will be sent to you.'
              : 'This address is required for tax receipts. No physical mail will be sent to you.'}
          </Text.Body>
          <Fields.Text
            label="Address Line One"
            required
            error={!!errors.address?.lineOne}
            helperText={errors?.address?.lineOne?.message}
            {...register('address.lineOne', { ...validations.required })}
          />
          <Fields.Text
            label="Address Line Two"
            error={!!errors.address?.lineTwo}
            helperText={errors?.address?.lineTwo?.message}
            {...register('address.lineTwo')}
          />
          <Fields.Text
            label="City"
            required
            error={!!errors.address?.city}
            helperText={errors?.address?.city?.message}
            {...register('address.city', { ...validations.required })}
          />

          {getValues('address.country') === 'Canada' ? (
            <FormControl fullWidth>
              <InputLabel id="provinceLabel">Province *</InputLabel>
              <Controller
                control={control}
                name="province"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <Select
                    inputProps={{ 'data-testid': 'province' }}
                    // @fixme It looks as if this were only added because the controller was given the wrong field name
                    value={watch('address.province')}
                    onChange={(e) => {
                      setValue('address.province', e.target.value, { shouldValidate: true })
                    }}
                    label="Province *"
                    labelId="provinceLabel"
                    aria-label="Province *"
                    error={!!errors.address?.province}
                    helpertext={errors?.address?.province?.message}
                    defaultValue=""
                    required
                  >
                    {PROVINCES_LIST.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          ) : (
            <Controller
              control={control}
              name="province"
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <Fields.Text
                  label="Province/State"
                  value={watch('address.province')}
                  onChange={(e) => {
                    setValue('address.province', e.target.value, { shouldValidate: true })
                  }}
                  required
                  error={!!errors.address?.province}
                  helperText={errors?.address?.province?.message}
                />
              )}
            />
          )}
          <Fields.Text
            label="Postal Code"
            required
            error={!!errors.address?.postalCode}
            helperText={errors?.address?.postalCode?.message}
            {...register('address.postalCode', { ...validations.required })}
          />
          <FormControl fullWidth>
            <InputLabel id="countryLabel">Country *</InputLabel>
            <Controller
              name="country"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <Select
                  inputProps={{ 'data-testid': 'country' }}
                  defaultValue="Canada"
                  value={watch('address.country')}
                  onChange={(e) => {
                    setValue('address.country', e.target.value, { shouldValidate: true })
                    setValue('address.province', '', { shouldValidate: true })
                  }}
                  label="Country *"
                  labelId="countryLabel"
                  aria-label="Country *"
                  error={!!errors.address?.country}
                  helpertext={errors?.address?.country?.message}
                >
                  {COUNTRY_LIST.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>

          {captchaSiteKey && <HCaptcha sitekey={captchaSiteKey} onVerify={setCaptchaToken} ref={captchaRef} />}
          <Text.Body data-testid="signupPrivacyMessage">
            Your information is kept private and secure. We do not sell your data. Learn more in our{' '}
            <a
              href="https://guide.givewise.ca/policy-and-user-documents/privacy-policy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </Text.Body>
          <Row justifyContent="flex-end" spacing={4}>
            <Text.Body>
              By clicking &ldquo;Sign Up&rdquo;, you agree to our{' '}
              <a
                href="https://guide.givewise.ca/policy-and-user-documents/terms-and-conditions"
                target="_blank"
                rel="noreferrer"
                data-testid="termConditionLink"
              >
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a
                href="https://guide.givewise.ca/policy-and-user-documents/privacy-policy"
                target="_blank"
                rel="noreferrer"
                data-testid="termConditionLink"
              >
                Privacy Policy
              </a>
            </Text.Body>
            <Button
              type="submit"
              disabled={loading}
              sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}
              data-testid="signUpButton"
            >
              Sign Up
            </Button>
            {errors.form && <FormHelperText error={!!errors.form}>{errors.form.message}</FormHelperText>}
          </Row>
          <Alert {...alertProps} />
        </Stack>
      </form>
      <Divider variant="middle" flexItem sx={{ mb: 4, mt: 4 }} />
      <Row
        justifyContent="flex-end"
        spacing={4}
        sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
      >
        <Text.Body data-testid="haveAccountText">Already have an account with us?</Text.Body>
        <Button color="info" to="/login" state={{ afterLogin }} data-testid="signInButton">
          Sign In
        </Button>
      </Row>
      <Row alignSelf="flex-end" justifyContent="flex-end" alignItems="center" spacing={4} pr={4} mt={4}>
        <Text.Body data-testid="haveAccountText">
          To delete a Giving Fund, please email <Link href="mailto:support@givewise.ca">support@givewise.ca</Link>
        </Text.Body>
      </Row>
    </ContentBox>
  )

  const charitySignUpMessage = () => (
    <Row alignItems="center" spacing={4}>
      <Stack spacing={2}>
        <Text.H5 data-testid="signupSuccessMessage">Charity Sign Up</Text.H5>
        <Text.Body>
          Charity Portal accounts are currently by invite only. For an invitation or to have your invitation resent,
          please email{' '}
          <Link href="mailto:charitysupport@givewise.ca?subject=Charity%20Portal%20Access%20Request">
            charitysupport@givewise.ca
          </Link>
        </Text.Body>
      </Stack>
    </Row>
  )

  const advisorSignUpMessage = () => (
    <Row alignItems="center" spacing={4}>
      <Stack spacing={2}>
        <Text.H5 data-testid="signupSuccessMessage">Financial Advisor Sign Up</Text.H5>
        <Text.Body>
          Financial Advisor accounts are currently by invite only. For an invitation or to have your invitation resent,
          please email{' '}
          <Link href="mailto:support@givewise.ca?subject=Financial%20Advisor%20Access%20Request">
            support@givewise.ca
          </Link>
        </Text.Body>
      </Stack>
    </Row>
  )

  const userSelector = () => {
    const brandColor = primaryColor || '#61bee8'
    const iconStyle = { fontSize: '48px', color: brandColor, backgroundColor: 'white' }
    return (
      <>
        {[
          { value: SIGNUP_TYPE.personal, name: 'Personal', icon: <CircleUser style={iconStyle} /> },
          { value: SIGNUP_TYPE.business, name: 'Business', icon: <Business style={iconStyle} /> },
          { value: SIGNUP_TYPE.charity, name: 'Charity', icon: <HandHoldingHeart style={iconStyle} /> },
          { value: SIGNUP_TYPE.advisor, name: 'Financial Advisor', icon: <BuildingColumns style={iconStyle} /> },
        ].map((u) => (
          <Card
            style={{
              width: '100%',
              borderLeft: `4px solid ${brandColor}`,
            }}
            onClick={() => {
              navigate({ search: location.search }, { state: { selectedSignupType: u.value } })
            }}
          >
            <CardActionArea to="route-name">
              <CardHeader
                avatar={
                  <Avatar
                    style={{
                      backgroundColor: 'white',
                      padding: '8px',
                      width: '52px',
                      height: '52px',
                    }}
                  >
                    {u.icon}
                  </Avatar>
                }
                title={u.name}
                titleTypographyProps={{ variant: 'h6', color: 'primary' }}
              />
            </CardActionArea>
          </Card>
        ))}
        <Divider variant="middle" flexItem sx={{ mb: 2, mt: 4 }} />
        <Row
          justifyContent="flex-end"
          spacing={4}
          sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
        >
          <Text.Body>{`Already have a ${platformName || 'GiveWise'} Giving Fund?`}</Text.Body>
          <Button to="/login" state={{ afterLogin }}>
            Sign In
          </Button>
        </Row>
      </>
    )
  }

  const mainForm = () => (
    <>
      <Text.H5 component="h1" data-testid="title">
        {`Join ${platformName || 'GiveWise Canada'}`}
      </Text.H5>
      <Text.Body data-testid="content">
        {selectedSignupType === SIGNUP_TYPE.personal || selectedSignupType === SIGNUP_TYPE.business
          ? `Create your giving fund today, and experience a new world of giving!`
          : `Please select the type of account you would like to create`}
      </Text.Body>
      {selectedSignupType === SIGNUP_TYPE.personal || selectedSignupType === SIGNUP_TYPE.business
        ? signUpForm()
        : userSelector()}
    </>
  )

  const renderContent = () => {
    if (selectedSignupType === SIGNUP_TYPE.charity) return charitySignUpMessage()
    if (selectedSignupType === SIGNUP_TYPE.advisor) return advisorSignUpMessage()
    return mainForm()
  }

  return (
    <Container maxWidth="sm">
      <Stack spacing={2} style={{ padding: 16 }}>
        {renderContent()}
      </Stack>
    </Container>
  )
}
